import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';
import { workflowEngineApi } from '@/redux/internal-services/workflowEngineApi';

// ─────────────────────────────── Enums ───────────────────────────────────────

export type WorkflowStatus = 'draft' | 'published' | 'archived';
export type LifecycleRole = 'entry' | 'intermediate' | 'terminal';
export type OperationalType = 'normal' | 'on_hold' | 'closed' | 'cancelled' | 'rejected';
export type TransitionCategory =
  | 'standard'
  | 'backward'
  | 'skip'
  | 'hold'
  | 'resume'
  | 'reopen'
  | 'cancel'
  | 'reject'
  | 'corrective_loop';
export type GateClassification = 'blocking' | 'warning';
export type TriggerPoint = 'on_enter' | 'on_exit' | 'on_dwell';
export type AutomationActionType =
  | 'create_task'
  | 'send_notification'
  | 'request_approval'
  | 'update_field'
  | 'trigger_module_action'
  | 'webhook';
export type AuthorityType =
  | 'transition'
  | 'approval'
  | 'reopen'
  | 'admin_correction'
  | 'system_exec';
export type AssignmentScope = 'any' | 'assigned_only';

// ───────────────────────────── Sub-models ────────────────────────────────────
// Field names match the MongoDB document schema (Pydantic StageDefinition, etc.)

export interface StageDefinition {
  stage_code: string;
  name: string;
  display_order: number;
  description?: string;
  is_custom?: boolean;
  tenant_label_renamable?: boolean;
}

export interface ProcessDefinition {
  process_code: string;
  name: string;
  stage_code: string;
  display_order: number;
  description?: string;
  is_custom?: boolean;
  tenant_label_renamable?: boolean;
}

export interface StatusDefinition {
  status_code: string;
  name: string;
  process_code: string;
  display_order: number;
  lifecycle_role: LifecycleRole;
  operational_type: OperationalType;
  is_optional?: boolean;
  is_custom?: boolean;
  is_active?: boolean;
  tenant_label_renamable?: boolean;
  description?: string;
}

export interface EntryPointDefinition {
  status_code: string;
  engagement_types: string[];
  description?: string;
}

export interface TransitionDefinition {
  transition_code: string;
  source_status_code: string;
  target_status_code: string;
  action_label: string;
  category: TransitionCategory;
  requires_reason?: boolean;
  is_optional?: boolean;
  is_custom?: boolean;
  description?: string;
}

export interface GovernanceGate {
  gate_code: string;
  scope: string;
  target_ref: string;
  classification: GateClassification;
  condition_type: string;
  condition_config?: Record<string, unknown>;
  failure_message?: string;
  required_next_action?: string;
  is_optional?: boolean;
  description?: string;
}

export interface ApprovalType {
  approval_type_code: string;
  name: string;
  approver_role: string;
  self_approval_allowed?: boolean;
  exposes_corrective_on_reject?: boolean;
  description?: string;
}

export interface AutomationHook {
  hook_code: string;
  trigger_point: string;
  trigger_ref: string;
  action_type: string;
  action_config?: Record<string, unknown>;
  is_mandatory?: boolean;
  is_optional?: boolean;
  description?: string;
}

export interface ModuleDisplay {
  binding_code: string;
  scope: 'status_entry';
  target_ref: string;
  module_code: string;
  is_active?: boolean;
  description?: string;
}

export interface AuthorityRule {
  rule_code: string;
  authority_type: AuthorityType;
  target_refs: string[];
  allowed_roles: string[];
  assignment_scope?: AssignmentScope;
  self_approval_allowed?: boolean;
  system_allowed?: boolean;
  description?: string;
}

// ────────────────────────────── Responses ────────────────────────────────────

export interface WorkflowDefinitionSummary {
  definition_id: string;
  version: number;
  name: string;
  description: string;
  status: WorkflowStatus;
  is_template: boolean;
  engagement_types: string[];
  stage_count: number;
  process_count: number;
  status_count: number;
  transition_count: number;
  gate_count: number;
  hook_count: number;
  authority_rule_count: number;
  published_at: string | null;
  created_on: string;
  updated_on: string;
  created_by: string | null;
}

export interface WorkflowDefinition extends WorkflowDefinitionSummary {
  stages: StageDefinition[];
  processes: ProcessDefinition[];
  statuses: StatusDefinition[];
  entry_points: EntryPointDefinition[];
  transitions: TransitionDefinition[];
  governance_gates: GovernanceGate[];
  approval_types: ApprovalType[];
  automation_hooks: AutomationHook[];
  authority_rules: AuthorityRule[];
  module_displays: ModuleDisplay[];
}

export interface WorkflowListResponse {
  success: boolean;
  message_code: number;
  data: WorkflowDefinitionSummary[];
  total: number;
}

export interface WorkflowDetailResponse {
  success: boolean;
  message_code: number;
  data: WorkflowDefinition;
}

export interface WorkflowImpactEntry {
  definition_id: string;
  version: number;
  name: string;
  status: WorkflowStatus;
}

export interface WorkflowImpactResponse {
  success: boolean;
  message_code: number;
  data: WorkflowImpactEntry[];
}

export interface CreateWorkflowRequest {
  // definition_id is never sent by the client — the server derives a unique slug from `name`.
  name: string;
  description?: string;
  is_template?: boolean;
  engagement_types: string[];
  stages: StageDefinition[];
  processes: ProcessDefinition[];
  statuses: StatusDefinition[];
  entry_points: EntryPointDefinition[];
  transitions: TransitionDefinition[];
  governance_gates: GovernanceGate[];
  approval_types?: ApprovalType[];
  automation_hooks: AutomationHook[];
  authority_rules: AuthorityRule[];
  module_displays?: ModuleDisplay[];
}

// ────────────────────────────── API Slice ────────────────────────────────────

export const workflowApi = createApi({
  reducerPath: 'workflowApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['WorkflowDefinition', 'WorkflowDefinitionList'],
  endpoints: (builder) => ({

    listWorkflowDefinitions: builder.query<
      WorkflowListResponse,
      { status?: WorkflowStatus; engagement_type?: string; is_template?: boolean; skip?: number; limit?: number }
    >({
      query: ({ status, engagement_type, is_template, skip = 0, limit = 50 }) => {
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        if (engagement_type) params.set('engagement_type', engagement_type);
        if (is_template !== undefined) params.set('is_template', String(is_template));
        params.set('skip', String(skip));
        params.set('limit', String(limit));
        return `/api/v1/platform/workflows?${params}`;
      },
      providesTags: ['WorkflowDefinitionList'],
    }),

    getWorkflowDefinition: builder.query<
      WorkflowDetailResponse,
      { definition_id: string; version: number }
    >({
      query: ({ definition_id, version }) =>
        `/api/v1/platform/workflows/${definition_id}/versions/${version}`,
      providesTags: (_result, _err, { definition_id, version }) => [
        { type: 'WorkflowDefinition', id: `${definition_id}@${version}` },
      ],
    }),

    getLatestPublished: builder.query<WorkflowDetailResponse, string>({
      query: (definition_id) => `/api/v1/platform/workflows/${definition_id}/latest`,
      providesTags: (_result, _err, definition_id) => [
        { type: 'WorkflowDefinition', id: `${definition_id}@latest` },
      ],
    }),

    createWorkflowDefinition: builder.mutation<WorkflowDetailResponse, CreateWorkflowRequest>({
      query: (body) => ({ url: '/api/v1/platform/workflows', method: 'POST', body }),
      invalidatesTags: ['WorkflowDefinitionList'],
    }),

    updateWorkflowDefinition: builder.mutation<
      WorkflowDetailResponse,
      { definition_id: string; version: number; body: CreateWorkflowRequest }
    >({
      query: ({ definition_id, version, body }) => ({
        url: `/api/v1/platform/workflows/${definition_id}/versions/${version}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _err, { definition_id, version }) => [
        'WorkflowDefinitionList',
        { type: 'WorkflowDefinition', id: `${definition_id}@${version}` },
      ],
    }),

    publishWorkflowDefinition: builder.mutation<
      WorkflowDetailResponse,
      { definition_id: string; version: number }
    >({
      query: ({ definition_id, version }) => ({
        url: `/api/v1/platform/workflows/${definition_id}/versions/${version}/publish`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _err, { definition_id, version }) => [
        'WorkflowDefinitionList',
        { type: 'WorkflowDefinition', id: `${definition_id}@${version}` },
        { type: 'WorkflowDefinition', id: `${definition_id}@latest` },
      ],
      // `invalidatesTags` above only reaches this file's OWN tags — workflowEngineApi is a
      // separate `createApi()` instance with its own cache, and every engagement's workflow
      // card/checklist reads the newly-published definition through ITS `WorkflowCustomization`
      // tag (getWorkflowCustomization), not through anything tagged here. Without this bridge, a
      // freshly published workflow only showed up after a hard reload happened to land with no
      // stale subscriber still holding the old cache (a race, not a fixed delay — hence "2-3
      // tries", not consistently 1). Same bridge pattern already used for SAM's REFRESH_WORKFLOW_SETTINGS
      // action (see useChatWorkspace.ts's refreshWorkflowSettingsCard).
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(workflowEngineApi.util.invalidateTags(['WorkflowCustomization']));
        } catch {
          // Publish failed — nothing to invalidate.
        }
      },
    }),

    // WFL-CFG-PLT-01-002 — publish a revised definition: creates a new draft version under the
    // SAME definition_id, seeded from this published version's structure.
    createNewWorkflowVersion: builder.mutation<
      WorkflowDetailResponse,
      { definition_id: string; version: number }
    >({
      query: ({ definition_id, version }) => ({
        url: `/api/v1/platform/workflows/${definition_id}/versions/${version}/new-version`,
        method: 'POST',
      }),
      invalidatesTags: ['WorkflowDefinitionList'],
    }),

    retireWorkflowDefinition: builder.mutation<
      WorkflowDetailResponse,
      { definition_id: string; version: number }
    >({
      query: ({ definition_id, version }) => ({
        url: `/api/v1/platform/workflows/${definition_id}/versions/${version}/retire`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _err, { definition_id, version }) => [
        'WorkflowDefinitionList',
        { type: 'WorkflowDefinition', id: `${definition_id}@${version}` },
        { type: 'WorkflowDefinition', id: `${definition_id}@latest` },
      ],
    }),

    deleteWorkflowDefinition: builder.mutation<
      { success: boolean; message_code: number },
      { definition_id: string; version: number }
    >({
      query: ({ definition_id, version }) => ({
        url: `/api/v1/platform/workflows/${definition_id}/versions/${version}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _err, { definition_id, version }) => [
        'WorkflowDefinitionList',
        { type: 'WorkflowDefinition', id: `${definition_id}@${version}` },
      ],
    }),

    // Creation wizard "Clone existing" / "Base template" starting points — creates a new draft
    // (v1) by deep-copying the source definition's stages/processes/statuses/transitions, with
    // governance gates/authority rules/automation hooks each copyable independently.
    cloneWorkflowDefinition: builder.mutation<
      WorkflowDetailResponse,
      {
        // new_definition_id is optional — the server derives a unique slug from `name` (or the
        // source definition's name) when omitted.
        definition_id: string; version: number; new_definition_id?: string; name?: string;
        copy_governance_gates?: boolean; copy_authority_rules?: boolean; copy_automation_hooks?: boolean;
      }
    >({
      query: ({ definition_id, version, ...body }) => ({
        url: `/api/v1/platform/workflows/${definition_id}/versions/${version}/clone`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['WorkflowDefinitionList'],
    }),

    // Transitions map impact-analysis panel — which other definitions reuse this status/process code.
    getWorkflowImpact: builder.query<
      WorkflowImpactResponse,
      { status_code?: string; process_code?: string; exclude_definition_id?: string }
    >({
      query: ({ status_code, process_code, exclude_definition_id }) => {
        const params = new URLSearchParams();
        if (status_code) params.set('status_code', status_code);
        if (process_code) params.set('process_code', process_code);
        if (exclude_definition_id) params.set('exclude_definition_id', exclude_definition_id);
        return `/api/v1/platform/workflows/impact?${params}`;
      },
    }),
  }),
});

export const {
  useListWorkflowDefinitionsQuery,
  useGetWorkflowDefinitionQuery,
  useGetLatestPublishedQuery,
  useCreateWorkflowDefinitionMutation,
  useUpdateWorkflowDefinitionMutation,
  usePublishWorkflowDefinitionMutation,
  useCreateNewWorkflowVersionMutation,
  useRetireWorkflowDefinitionMutation,
  useDeleteWorkflowDefinitionMutation,
  useCloneWorkflowDefinitionMutation,
  useGetWorkflowImpactQuery,
} = workflowApi;
